'use client'

import {
  HTMLAttributes,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import clsx from 'clsx'

import {
  autoCompletePostalCode,
  validatePostalCodeField,
  validatePostalCodeValues
} from 'modules/customer'
import Button from 'shared/ui/Button/Button'
import { Collapse } from 'shared/ui/Collapse'
import Input from 'shared/ui/form/Input'
import SelectDropdown from 'shared/ui/form/Select'
import toast from 'shared/ui/utils/toast'

import useQueryDeliveryCities from '../queries/useQueryDeliveryCities'
import { DeliveryArea } from '../submodules/area'

import ReportMissingDeliveryAreaModal from './ReportMissingDeliveryAreaModal'

import styles from './DeliveryAreaForm.module.scss'

const VALUE_MISSING_CITY_VALUE = 'VALUE_MISSING_CITY_VALUE'

// Input / SelectDropdown forward `className` via `...rest` without declaring it, hence the spread
const inputClassName = { className: styles.postalCodeInput }
const selectDisabledClassName = { className: styles.citySelectDisabled }

interface DeliveryAreaFormProps
  extends Omit<HTMLAttributes<HTMLFormElement>, 'onSubmit' | 'onChange'> {
  initValues: DeliveryArea
  isLoading: boolean
  onSubmit: (values: DeliveryArea) => Promise<void>
  onChange?: (values: DeliveryArea) => void
  buttonText: string
  focusOnPostalCodeInput?: boolean
  deliveryPossible?: boolean | null
}

export default function DeliveryAreaForm({
  initValues,
  isLoading,
  onSubmit,
  onChange,
  buttonText,
  focusOnPostalCodeInput = true,
  deliveryPossible = null,
  className,
  ...props
}: DeliveryAreaFormProps) {
  const [submitted, setSubmitted] = useState(false)
  const [initializated, setInitializated] = useState(false)
  const [values, setValues] = useState<DeliveryArea>(initValues)
  const [validPostalCode, setValidPostlCode] = useState(false)
  const [modalReportMissingCityOpen, setModalReportMissingCityOpen] =
    useState(false)
  const autoHandledPostalCodeRef = useRef<string | null>(null)

  const handleMissingCitySubmited = useCallback(
    async (reportedValues: DeliveryArea) => {
      onChange?.(reportedValues)
      setValues({
        postalCode: reportedValues.postalCode,
        city: ''
      })
      setModalReportMissingCityOpen(false)

      await onSubmit(reportedValues)

      setTimeout(() => {
        setSubmitted(true)
      }, 0)
    },
    [onChange, onSubmit]
  )

  const {
    data: cities,
    isLoading: isLoadingCities,
    isFetching: isFetchingCities,
    isError: isCitiesError,
    errorUpdatedAt: citiesFetchErrorUpdatedAtRef,
    error: citiesError
  } = useQueryDeliveryCities(values.postalCode)

  useEffect(() => {
    if (
      !validPostalCode ||
      isLoadingCities ||
      isFetchingCities ||
      cities === undefined
    ) {
      return
    }

    const postalCode = values.postalCode

    if (cities.length === 0) {
      if (autoHandledPostalCodeRef.current === postalCode) return

      autoHandledPostalCodeRef.current = postalCode
      void handleMissingCitySubmited({ postalCode, city: '' })

      return
    }

    autoHandledPostalCodeRef.current = null

    if (values.city && !cities.includes(values.city)) {
      const newValues = { postalCode, city: '' }

      setValues(newValues)
      onChange?.(newValues)

      return
    }

    const savedCityMatchesPostalCode =
      initValues.postalCode === postalCode && initValues.city

    if (
      savedCityMatchesPostalCode &&
      cities.includes(initValues.city) &&
      !values.city
    ) {
      const newValues = { postalCode, city: initValues.city }

      setValues(newValues)
      onChange?.(newValues)
    }
  }, [
    cities,
    handleMissingCitySubmited,
    initValues.city,
    initValues.postalCode,
    isFetchingCities,
    isLoadingCities,
    onChange,
    validPostalCode,
    values.city,
    values.postalCode
  ])

  useEffect(() => {
    if (isCitiesError) {
      toast.error(
        'Nie udało się pobrać listy miejscowości dla podanego kodu pocztowego'
      )
    }
  }, [isCitiesError, citiesError, citiesFetchErrorUpdatedAtRef])

  useEffect(() => {
    if (!initializated) {
      setValues(initValues)
      setInitializated(true)
    }
  }, [initValues, initializated])

  useEffect(() => {
    return () => {
      setValues({
        postalCode: '',
        city: ''
      })
    }
  }, [])

  useEffect(() => {
    if (values.postalCode) {
      validatePostalCodeValues(values, setValidPostlCode)
    } else {
      setValidPostlCode(false)
    }
  }, [values])

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    if (!validPostalCode) return

    await onSubmit(values)
    setTimeout(() => {
      setSubmitted(true)
    }, 0)
  }

  const handleChangePostalCode = useCallback(
    (_: string, postalCode: string) => {
      const AutoCompletePostalCode = autoCompletePostalCode(postalCode)

      const newValues = { postalCode: AutoCompletePostalCode, city: '' }

      autoHandledPostalCodeRef.current = null
      setSubmitted(false)
      onChange?.(newValues)
      setValues(newValues)
    },
    [onChange]
  )

  const handleCityChange = useCallback(
    (_: string, value: string | string[]) => {
      const city = Array.isArray(value) ? (value[0] ?? '') : value

      if (city === VALUE_MISSING_CITY_VALUE) {
        return setModalReportMissingCityOpen(true)
      }

      setValues(prev => {
        const newValues = { ...prev, city }

        onChange?.(newValues)

        return newValues
      })
    },
    [onChange]
  )

  const cityOptions = useMemo(() => {
    const reportMissingCityOption = {
      label: 'Zgłoś swoją miejscowość',
      value: VALUE_MISSING_CITY_VALUE
    }

    if (isLoadingCities || isFetchingCities) {
      return []
    }

    const citiesOptions =
      cities?.map(city => ({
        label: city,
        value: city
      })) || []

    const options = [...citiesOptions, reportMissingCityOption]

    return options
  }, [cities, isLoadingCities, isFetchingCities])

  const getCitiesDropdownLabel = () => {
    if (!validPostalCode) return 'Uzupełnij kod pocztowy'

    if (isLoadingCities || isFetchingCities) return 'Pobieranie miejscowości...'

    if (cities?.length === 0) return 'Nie dostarczamy pod wskazany kod pocztowy'

    return 'Wybierz miejscowość'
  }

  const isCityDisabled = isLoadingCities || isFetchingCities || !validPostalCode

  return (
    <>
      <form
        noValidate
        onSubmit={handleSubmit}
        {...props}
        className={clsx(styles.formWrapper, className)}
      >
        <div className={clsx('postalCodeForm-inputsWrapper', styles.inputWrapper)}>
          <Input
            {...inputClassName}
            required
            noMargin
            name='postalCode'
            label='Kod pocztowy'
            size='medium'
            value={values.postalCode}
            placeholder='xx-xxx'
            onChange={handleChangePostalCode}
            validate={validatePostalCodeField(values)}
            autoFocus={focusOnPostalCodeInput}
            inputMode='numeric'
            type='tel'
          />
          <SelectDropdown
            {...(isCityDisabled ? selectDisabledClassName : {})}
            disabled={isCityDisabled}
            variant='primary'
            noMargin
            size='medium'
            options={cityOptions}
            name='city'
            value={values.city}
            onChange={handleCityChange}
            label={getCitiesDropdownLabel()}
            id='city-select'
          />
        </div>

        <Button
          className={styles.submitButton}
          variant='primary'
          type='submit'
          disabled={!validPostalCode || isLoading || !values.city}
          loading={isLoading}
        >
          {buttonText}
        </Button>
      </form>
      <Collapse open={submitted && deliveryPossible !== null}>
        <div
          className={clsx(styles.message, {
            [styles.messagePossible]: deliveryPossible ?? false
          })}
          aria-live='polite'
          role='status'
        >
          {deliveryPossible === true && 'Obsługujemy wybrany obszar'}
          {deliveryPossible === false &&
            'Jeszcze nie obsługujemy wybranego obszaru.'}
          {deliveryPossible === false && (
            <Button
              className={styles.reportButton}
              size='small'
              onClick={() => setModalReportMissingCityOpen(true)}
            >
              Zgłoś nam swój adres.{' '}
            </Button>
          )}
        </div>
      </Collapse>
      <ReportMissingDeliveryAreaModal
        open={modalReportMissingCityOpen}
        onClose={() => setModalReportMissingCityOpen(false)}
        onSubmit={handleMissingCitySubmited}
        postalCode={values.postalCode}
        city={''}
      />
    </>
  )
}
